import { Controller } from "@hotwired/stimulus"

// Combines the *view* mode (which tab is selected — owned by query-builder)
// with *textarea syntax detection* (does the content start with "from logs")
// into a single derived "query mode" signal. Other parts of the page (filter
// buttons in the drawer, etc.) consume this to decide whether modifying the
// query is safe. Also feeds the "ES|QL detected" slot of the state pill via
// outlet — the pill owns its own visibility, this controller just publishes
// truthiness.
export default class extends Controller {
  // Two consumers wire this controller differently:
  //   - accounts/logs search bar uses the logs--state-pill outlet (preferred)
  //   - admin/soc/agents/logs search bar uses a direct esqlQueryTypeLabel target
  // Whichever is wired, #refresh updates that surface.
  static targets = ["queryText", "esqlQueryTypeLabel"]
  static outlets = ["logs--state-pill"]

  connect() {
    this.viewMode = document.body.dataset.logsViewMode || "esql"
    this.#refresh(this.queryTextTarget.value)
  }

  // Wired via data-action="input->accounts--logs--query-type#textareaChanged" on the
  // textarea. `input` fires for every mutation (typing, paste, programmatic
  // changes from filter buttons) — keeps this in lockstep with the composer's
  // dirty tracking.
  textareaChanged() {
    this.#refresh(this.queryTextTarget.value)
  }

  update(event) {
    this.#refresh(event.currentTarget.dataset.inputContent)
  }

  // Wired via data-action="logs:view-mode-changed@document->accounts--logs--query-type#viewModeChanged"
  // on the controller's host element.
  viewModeChanged(event) {
    this.viewMode = event.detail?.mode
    this.#refresh(this.queryTextTarget.value)
  }

  #refresh(textValue) {
    const trimmed = (textValue || "").trim()
    // Word-boundary match so "from logs_extra" / "from logs_backup" don't
    // qualify — the composer's empty-textarea path hardcodes "FROM logs".
    const isEsqlSyntax = /^from\s+logs\b/i.test(trimmed)
    // Empty is editable as ES|QL too — composer prepends "FROM logs" for the
    // first filter.
    const canEditAsEsql = isEsqlSyntax || trimmed === ""

    if (this.hasLogsStatePillOutlet) {
      this.logsStatePillOutlet.esqlValue = isEsqlSyntax
    } else if (this.hasEsqlQueryTypeLabelTarget) {
      this.esqlQueryTypeLabelTarget.classList.toggle("hidden", !isEsqlSyntax)
    }

    this.#broadcast(this.#deriveQueryMode(canEditAsEsql))
  }

  #deriveQueryMode(canEditAsEsql) {
    if (this.viewMode === "esql") return canEditAsEsql ? "esql" : "non-esql"
    return this.viewMode || "non-esql"
  }

  #broadcast(mode) {
    if (document.body.dataset.logsQueryMode === mode) return
    document.body.dataset.logsQueryMode = mode
    document.dispatchEvent(new CustomEvent("logs:query-mode-changed", { detail: { mode } }))
  }
}
